import React from 'react';
import { SeatsChart } from 'services/Seats';
import { ChartItem } from './components/ChartItem/ChartItem';
import {
	ChartsSection,
	ChartsSectionTitle,
	ChartsSectionDescription,
	ChartsListContainer,
	LoadingState,
	ErrorState,
} from './SeatsCharts.styles';
import { useGetSeatsCharts } from 'hooks/useGetSeatsCharts';

interface SeatsChartsListProps {
	onChartSelect?: (chart: SeatsChart) => void;
	selectedChartId?: string;
	title?: string;
	/** Optional muted sub-label rendered under the title. */
	description?: string;
	/**
	 * Freeze the selection (e.g. once tickets have sold): the selected chart stays highlighted
	 * but cannot be changed, and the other charts are greyed out and non-interactive.
	 */
	locked?: boolean;
}

export const SeatsChartsList: React.FC<SeatsChartsListProps> = ({
	onChartSelect,
	selectedChartId,
	title = 'Seat Charts',
	description,
	locked = false,
}) => {
	const { data: charts, isLoading, error } = useGetSeatsCharts();

	const handleChartSelect = (chart: SeatsChart) => {
		onChartSelect?.(chart);
	};

	// Only the inner body varies by state; the section shell + title + description are rendered once.
	let body: React.ReactNode;
	if (isLoading) {
		body = <LoadingState>Loading seat charts...</LoadingState>;
	} else if (error) {
		body = <ErrorState>Failed to load seat charts. Please try again.</ErrorState>;
	} else if (charts.length === 0) {
		body = <LoadingState>No seat charts available</LoadingState>;
	} else {
		body = (
			<ChartsListContainer>
				{charts.map((chart) => (
					<ChartItem
						key={chart.id}
						chart={chart}
						selected={selectedChartId === chart.key}
						locked={locked}
						onSelect={handleChartSelect}
					/>
				))}
			</ChartsListContainer>
		);
	}

	return (
		<ChartsSection>
			<ChartsSectionTitle>{title}</ChartsSectionTitle>
			{description && <ChartsSectionDescription>{description}</ChartsSectionDescription>}
			{body}
		</ChartsSection>
	);
};
